import { useQuery } from '@apollo/client';
import { Box, Button, IconButton, Typography } from '@material-ui/core';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import HourglassEmptyIcon from '@material-ui/icons/HourglassEmpty';
import MicIcon from '@material-ui/icons/Mic';
import PauseIcon from '@material-ui/icons/Pause';
import PlayArrowIcon from '@material-ui/icons/PlayArrow';
import StopIcon from '@material-ui/icons/Stop';
import { Palette } from '@woundtech/ui-colors';
import {
  ALLOW_MULTIPLE_RECORDINGS_FLAG,
  RELEASE_WOUNDSCRIBE_ENABLED_FLAG,
} from 'common/constants';
import { useLDFlagValue } from 'common/useLDFlagValue';
import { EncounterState } from 'encounter/constants';
import { EncounterWithStatus } from 'encounter/Schema';
import React, { useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { WOUNDSCRIBE_MIC_STATES } from 'woundscribe/core/constants';
import { useAudioEviction } from 'woundscribe/hooks/useAudioEviction';
import { useMaxDurationStopNavigation } from 'woundscribe/hooks/useMaxDurationStopNavigation';
import { useWoundScribeConsent } from 'woundscribe/hooks/useWoundScribeConsent';
import { MicState } from 'woundscribe/types';
import {
  cancelWoundScribeStart,
  dismissStopConfirmation,
  ensureWoundScribeEncounter,
  formatElapsed,
  isCurrentRecordingOnEncounter,
  isRecordingOnOtherEncounter,
  pauseWoundScribeRecording,
  requestStopConfirmation,
  resumeWoundScribeRecording,
  startWoundScribeRecording,
  stopWoundScribeRecording,
  useWoundScribeStore,
} from '../core/recordingStore';
import {
  getEncounterIdFromPath,
  getMicBottomBarSubtitle,
  getMicPauseResumeLabels,
  getMicStatusLabel,
  getMicStopLabels,
  getPatientIdFromPath,
  isStartBlockedByActiveRecording,
  isStartBlockedByTranscripts,
  pathHasEncounter,
  pathHasWoundScribe,
} from '../core/utils';
import StopConfirmationDialog from './StopConfirmationDialog';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      position: 'fixed',
      bottom: 0,
      left: 'var(--woundscribe-footer-left, 0px)',
      right: 0,
      width: 'auto',
      transition: theme.transitions.create('left', {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.enteringScreen,
      }),
      zIndex: theme.zIndex.drawer,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      minHeight: 48,
      paddingLeft: theme.spacing(2),
      paddingRight: theme.spacing(1),
      borderRadius: 4,
      backgroundColor: theme.palette.primary.main,
      color: Palette.Base.White,
      boxShadow: '0 -2px 8px rgba(0,0,0,0.12)',
    },
    left: {
      display: 'flex',
      alignItems: 'center',
      minWidth: 0,
      flexGrow: 1,
      marginRight: theme.spacing(1.5),
    },
    title: {
      fontWeight: 600,
      fontSize: 14,
      letterSpacing: 0.2,
      whiteSpace: 'nowrap',
    },
    subtitle: {
      marginLeft: theme.spacing(1.5),
      fontSize: 12,
      opacity: 0.85,
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      fontStyle: 'italic',
    },
    subtitleLive: {
      overflowX: 'auto',
      textOverflow: 'clip',
      scrollbarWidth: 'none',
      '&::-webkit-scrollbar': {
        display: 'none',
      },
    },
    subtitleAlert: {
      opacity: 1,
      fontWeight: 600,
      fontStyle: 'normal',
    },
    subtitleInterim: {
      color: '#FFE082',
    },
    right: {
      display: 'flex',
      alignItems: 'center',
      flexShrink: 0,
    },
    chip: {
      display: 'inline-flex',
      alignItems: 'center',
      height: 32,
      paddingLeft: theme.spacing(1.25),
      paddingRight: 2,
      marginRight: theme.spacing(1),
      borderRadius: 16,
      backgroundColor: 'rgba(255,255,255,0.14)',
      border: '1px solid rgba(255,255,255,0.22)',
    },
    chipRecording: {
      backgroundColor: 'rgba(239, 83, 80, 0.22)',
      borderColor: 'rgba(239, 83, 80, 0.55)',
    },
    chipPaused: {
      backgroundColor: 'rgba(255, 193, 7, 0.22)',
      borderColor: 'rgba(255, 193, 7, 0.55)',
    },
    chipProcessing: {
      backgroundColor: 'rgba(255,255,255,0.12)',
      borderColor: 'rgba(255,255,255,0.2)',
    },
    dot: {
      width: 8,
      height: 8,
      borderRadius: '50%',
      backgroundColor: Palette.Red.Light,
      flexShrink: 0,
      marginRight: theme.spacing(0.75),
      animation: '$blink 1.2s ease-in-out infinite',
    },
    '@keyframes blink': {
      '0%, 100%': { opacity: 1 },
      '50%': { opacity: 0.35 },
    },
    label: {
      color: Palette.Base.White,
      fontSize: 12,
      fontWeight: 600,
      letterSpacing: 0.2,
      whiteSpace: 'nowrap',
      lineHeight: 1,
      marginRight: theme.spacing(0.75),
    },
    timer: {
      color: Palette.Base.White,
      fontSize: 12,
      fontWeight: 700,
      fontVariantNumeric: 'tabular-nums',
      letterSpacing: 0.4,
      whiteSpace: 'nowrap',
      lineHeight: 1,
      minWidth: 40,
      marginRight: theme.spacing(0.25),
    },
    timerActive: {
      color: Palette.Red.Light,
    },
    timerPaused: {
      color: '#FFD54F',
    },
    action: {
      color: Palette.Base.White,
      padding: 4,
      marginLeft: 2,
      '&:hover': {
        backgroundColor: 'rgba(255,255,255,0.12)',
      },
    },
    actionDanger: {
      color: Palette.Red.Light,
    },
    actionIcon: {
      fontSize: 18,
    },
    idleButton: {
      color: Palette.Base.White,
      padding: 8,
    },
    idleButtonDisabled: {
      opacity: 0.55,
    },
    startLabel: {
      marginRight: theme.spacing(0.5),
      fontSize: 13,
      fontWeight: 600,
    },
  })
);

const WoundScribeFooterBar: React.FC = () => {
  const classes = useStyles();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const isWoundScribeEnabled = useLDFlagValue(RELEASE_WOUNDSCRIBE_ENABLED_FLAG);
  const hasEncounterInUrl = pathHasEncounter(pathname);
  const hasWoundScribeInUrl = pathHasWoundScribe(pathname);
  const encounterId = getEncounterIdFromPath(pathname);
  const patientId = getPatientIdFromPath(pathname);
  const {
    state,
    elapsedMs,
    toast,
    liveTranscript = '',
    provisionalTranscript = '',
    isStopConfirmOpen,
    lastCompletedStop,
    displayedTranscript,
    existingTranscripts = [],
    isLoadingExistingTranscripts = false,
    existingTranscriptsError = null,
    recordingEligibility = null,
    isLoadingRecordingEligibility = false,
    localOrphanedRecordingIds = [],
    isAwaitingLocationPermission = false,
    encounterId: barEncounterId,
  } = useWoundScribeStore();
  const allowMultipleRecordings =
    useLDFlagValue(ALLOW_MULTIPLE_RECORDINGS_FLAG) === true;
  const subtitleRef = useRef<HTMLDivElement>(null);
  const isSessionActive = state !== WOUNDSCRIBE_MIC_STATES.IDLE;

  const isRecordingElsewhere = isRecordingOnOtherEncounter(
    { state, encounterId: barEncounterId },
    encounterId
  );
  // The open page is the encounter the take in the store was started on.
  const isOnRecordingEncounter =
    Boolean(encounterId) && encounterId === barEncounterId;

  // Rebinds only when idle, so the store follows the open page again once a take on
  // another encounter has finished. `state` is a dependency for that reason.
  useEffect(() => {
    if (!isWoundScribeEnabled || state !== WOUNDSCRIBE_MIC_STATES.IDLE) {
      return;
    }
    ensureWoundScribeEncounter(encounterId, hasEncounterInUrl, patientId);
  }, [isWoundScribeEnabled, hasEncounterInUrl, encounterId, patientId, state]);

  // Once a recording is in progress it must stay visible/controllable on every
  // page; the check-in/e-sign gate below only applies while idle on the
  // encounter's own page (deciding whether the Start button may be shown).
  const shouldCheckEncounterStatus =
    isWoundScribeEnabled &&
    !isSessionActive &&
    hasEncounterInUrl &&
    Boolean(encounterId);

  const { data: encounterStatusData } = useQuery(EncounterWithStatus, {
    fetchPolicy: 'no-cache',
    skip: !shouldCheckEncounterStatus,
    variables: { id: encounterId },
  });

  const { hasConsent } = useWoundScribeConsent(
    patientId,
    encounterId,
    isWoundScribeEnabled === true
  );

  // Runs above the early returns below, so pending evictions still run when the
  // footer hides itself (for example after a stop has saved the encounter's transcript).
  useAudioEviction();

  // The 75-minute auto stop has no UI of its own, so the footer moves the user to
  // Completed Recording.
  useMaxDurationStopNavigation(lastCompletedStop, () => {
    // Only when the open page is the encounter the take was started on.
    if (hasWoundScribeInUrl && isOnRecordingEncounter) {
      const ordersLink = pathname?.split('/woundscribe') || '';
      navigate(`${ordersLink[0]}/woundscribe/complete-recording`);
    }
  });

  const encounterState = encounterStatusData?.EncounterState?.status;
  const isEncounterESigned = encounterState === EncounterState.eSigned;
  const isEncounterCheckedIn =
    encounterState === EncounterState.checkedIn ||
    encounterState === EncounterState.inProgress;

  const isRecordingOrPaused =
    state === WOUNDSCRIBE_MIC_STATES.RECORDING ||
    state === WOUNDSCRIBE_MIC_STATES.PAUSED;

  const subtitle = getMicBottomBarSubtitle(state, toast, liveTranscript);
  const showingLiveTranscript =
    Boolean(liveTranscript.trim()) &&
    isRecordingOrPaused &&
    subtitle === liveTranscript.trim();
  const liveCaption = showingLiveTranscript
    ? splitLiveCaption(liveTranscript, provisionalTranscript)
    : null;

  useEffect(() => {
    const node = subtitleRef.current;
    if (!node || !showingLiveTranscript || !liveTranscript) {
      return;
    }
    node.scrollLeft = node.scrollWidth;
  }, [showingLiveTranscript, liveTranscript]);

  // The flag blocks starting a recording. It must never hide an active session,
  // because Stop is the only way to end one.
  if (!isWoundScribeEnabled && !isSessionActive) {
    return null;
  }

  const isStartBlockedByTranscriptState = isStartBlockedByTranscripts({
    allowMultipleRecordings,
    hasCompletedRecording: isCurrentRecordingOnEncounter(
      displayedTranscript,
      encounterId
    ),
    existingTranscriptCount: existingTranscripts.length,
    isLoadingExistingTranscripts,
    hasFetchError: existingTranscriptsError !== null,
  });
  // Not gated by the ws-allow-multiple-recordings flag — see isStartBlockedByActiveRecording.
  const isStartBlockedByActiveRecordingState = isStartBlockedByActiveRecording({
    recordingEligibility,
    isLoadingRecordingEligibility,
    hasLocalOrphanedRecordings: localOrphanedRecordingIds.length > 0,
  });

  const shouldShowWhileIdle =
    hasEncounterInUrl &&
    hasWoundScribeInUrl &&
    !isEncounterESigned &&
    isEncounterCheckedIn &&
    hasConsent &&
    !isStartBlockedByTranscriptState &&
    !isStartBlockedByActiveRecordingState;

  if (!isSessionActive && !shouldShowWhileIdle) {
    return null;
  }

  const isPermissionPending =
    isAwaitingLocationPermission ||
    (Boolean(toast) && /requesting microphone/i.test(toast || ''));
  const hasStatusToast = Boolean(toast);

  async function handleStartRecording() {
    await startWoundScribeRecording();
    if (hasWoundScribeInUrl) {
      const ordersLink = pathname?.split('/woundscribe') || '';
      navigate(`${ordersLink[0]}/woundscribe/active-recording`);
    }
  }

  async function handleConfirmStop() {
    // Read before the stop, while the store still holds the take's encounter.
    const shouldOpenCompletedRecording =
      hasWoundScribeInUrl && isOnRecordingEncounter;
    dismissStopConfirmation();
    await stopWoundScribeRecording({ reason: 'manual' });

    // Only within /woundscribe, and only on the encounter the take was started on.
    // A stop from another encounter keeps the user where they are.
    // Audio eviction runs separately (useAudioEviction), so navigation does not wait for it.
    if (shouldOpenCompletedRecording) {
      const ordersLink = pathname?.split('/woundscribe') || '';
      navigate(`${ordersLink[0]}/woundscribe/complete-recording`);
    }
  }

  const getControlsClasses = () => {
    switch (state) {
      case WOUNDSCRIBE_MIC_STATES.RECORDING:
        return {
          chipClass: classes.chipRecording,
          timerClass: classes.timerActive,
        };
      case WOUNDSCRIBE_MIC_STATES.PAUSED:
        return {
          chipClass: classes.chipPaused,
          timerClass: classes.timerPaused,
        };
      case WOUNDSCRIBE_MIC_STATES.PROCESSING:
        return { chipClass: classes.chipProcessing, timerClass: '' };
      default:
        return { chipClass: '', timerClass: '' };
    }
  };

  const { chipClass, timerClass } = getControlsClasses();

  const subtitleClasses = `${classes.subtitle} ${
    showingLiveTranscript ? classes.subtitleLive : ''
  } ${hasStatusToast ? classes.subtitleAlert : ''}`;

  const renderIdleControls = () => (
    <>
      <Typography
        className={classes.startLabel}
        variant="body2"
        component="span"
      >
        Start
      </Typography>
      <IconButton
        className={`${classes.idleButton} ${
          isPermissionPending ? classes.idleButtonDisabled : ''
        }`}
        color="inherit"
        aria-label="Start WoundScribe"
        title={
          isPermissionPending
            ? 'Waiting for microphone permission…'
            : 'Start WoundScribe'
        }
        disabled={isPermissionPending}
        onClick={() => handleStartRecording()}
      >
        <MicIcon />
      </IconButton>
      {isAwaitingLocationPermission && (
        <Button
          size="small"
          color="inherit"
          onClick={() => cancelWoundScribeStart()}
        >
          Cancel
        </Button>
      )}
    </>
  );

  const renderActiveControls = () => {
    const isProcessing = state === WOUNDSCRIBE_MIC_STATES.PROCESSING;
    const pauseResume = getMicPauseResumeLabels(state);
    const stopLabels = getMicStopLabels(state, formatElapsed(elapsedMs));

    return (
      <Box
        className={`${classes.chip} ${chipClass}`}
        role="status"
        aria-live="polite"
      >
        {state === WOUNDSCRIBE_MIC_STATES.RECORDING ? (
          <Box className={classes.dot} />
        ) : null}
        <Typography
          className={classes.label}
          variant="caption"
          component="span"
        >
          {getMicStatusLabel(state)}
        </Typography>
        <Typography
          className={`${classes.timer} ${timerClass}`}
          variant="caption"
          component="span"
        >
          {formatElapsed(elapsedMs)}
        </Typography>

        {!isProcessing && (
          <IconButton
            className={classes.action}
            aria-label={pauseResume.ariaLabel}
            title={pauseResume.title}
            size="small"
            onClick={() => handlePauseResume(state)}
          >
            {state === WOUNDSCRIBE_MIC_STATES.RECORDING ? (
              <PauseIcon className={classes.actionIcon} />
            ) : (
              <PlayArrowIcon className={classes.actionIcon} />
            )}
          </IconButton>
        )}

        <IconButton
          className={`${classes.action} ${
            isProcessing ? '' : classes.actionDanger
          }`}
          aria-label={stopLabels.ariaLabel}
          title={stopLabels.title}
          size="small"
          disabled={isProcessing}
          onClick={() => requestStopConfirmation()}
        >
          {isProcessing ? (
            <HourglassEmptyIcon className={classes.actionIcon} />
          ) : (
            <StopIcon className={classes.actionIcon} />
          )}
        </IconButton>
      </Box>
    );
  };

  const renderControls = () => {
    switch (state) {
      case WOUNDSCRIBE_MIC_STATES.IDLE:
        return renderIdleControls();
      case WOUNDSCRIBE_MIC_STATES.RECORDING:
      case WOUNDSCRIBE_MIC_STATES.PAUSED:
      case WOUNDSCRIBE_MIC_STATES.PROCESSING:
        return renderActiveControls();
      default:
        return null;
    }
  };

  return (
    <>
      <Box
        className={classes.root}
        component="footer"
        data-testid="wound-scribe-bottom-bar"
        role="region"
        aria-label="WoundScribe"
      >
        <Box className={classes.left}>
          {isRecordingElsewhere ? (
            <Box
              className={`${classes.chip} ${classes.chipRecording}`}
              data-testid="woundscribe-other-encounter-indicator"
              title="A recording is in progress for another encounter"
              role="status"
            >
              <Box className={classes.dot} />
              <Typography
                className={classes.label}
                variant="caption"
                component="span"
              >
                Recording in another encounter
              </Typography>
            </Box>
          ) : (
            <Typography
              className={classes.title}
              variant="subtitle2"
              component="span"
            >
              WoundScribe
            </Typography>
          )}
          <Box
            ref={subtitleRef}
            className={subtitleClasses}
            title={showingLiveTranscript ? liveTranscript : subtitle}
            role="status"
            aria-live="polite"
          >
            {liveCaption ? (
              <>
                {liveCaption.confirmed ? (
                  <span data-testid="woundscribe-transcript-confirmed">
                    {liveCaption.confirmed}
                  </span>
                ) : null}
                {liveCaption.interim ? (
                  <span
                    className={classes.subtitleInterim}
                    data-testid="woundscribe-transcript-interim"
                  >
                    {liveCaption.interim}
                  </span>
                ) : null}
              </>
            ) : (
              subtitle
            )}
          </Box>
        </Box>

        <Box className={classes.right}>{renderControls()}</Box>
      </Box>
      <StopConfirmationDialog
        open={isStopConfirmOpen}
        onConfirm={() => handleConfirmStop()}
        onCancel={() => dismissStopConfirmation()}
        onPause={
          state === WOUNDSCRIBE_MIC_STATES.RECORDING
            ? handlePauseFromConfirmation
            : undefined
        }
      />
    </>
  );
};

/** Confirmed words stay in the bar color; the unconfirmed tail is styled apart. */
function splitLiveCaption(
  live: string,
  provisional = ''
): { confirmed: string; interim: string } {
  const hasPartial = provisional.length > 0 && live.endsWith(provisional);
  const confirmedRaw = hasPartial
    ? live.slice(0, live.length - provisional.length)
    : live;
  const interimRaw = hasPartial ? provisional : '';

  if (!interimRaw) {
    return { confirmed: confirmedRaw.trim(), interim: '' };
  }

  const confirmed = confirmedRaw.trimStart();
  if (!confirmed.trim()) {
    return { confirmed: '', interim: interimRaw.trim() };
  }

  return { confirmed, interim: interimRaw.trimEnd() };
}

function handlePauseFromConfirmation() {
  dismissStopConfirmation();
  pauseWoundScribeRecording();
}

function handlePauseResume(state: MicState) {
  switch (state) {
    case WOUNDSCRIBE_MIC_STATES.RECORDING:
      pauseWoundScribeRecording();
      break;
    case WOUNDSCRIBE_MIC_STATES.PAUSED:
      resumeWoundScribeRecording();
      break;
    default:
      break;
  }
}

export default React.memo(WoundScribeFooterBar);
